import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Calendar, CheckCircle, XCircle, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { motion } from "framer-motion";

const leaveTypes = {
  annual: "Yıllık İzin",
  sick: "Hastalık İzni",
  unpaid: "Ücretsiz İzin",
  maternity: "Doğum İzni",
  marriage: "Evlilik İzni",
  bereavement: "Vefat İzni"
};

const statusColors = {
  pending: "bg-amber-400/10 text-amber-400",
  approved: "bg-emerald-400/10 text-emerald-400",
  rejected: "bg-rose-400/10 text-rose-400"
};

const statusLabels = {
  pending: "Bekliyor",
  approved: "Onaylandı",
  rejected: "Reddedildi"
};

const Leaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    type: "annual",
    start_date: "",
    end_date: "",
    days: 1,
    status: "pending",
    notes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [leavesRes, employeesRes] = await Promise.all([
        axios.get(`${API}/leaves`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true })
      ]);
      setLeaves(leavesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const calculateDays = (start, end) => {
    if (!start || !end) return 1;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 1;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        days: calculateDays(formData.start_date, formData.end_date)
      };
      
      if (editingLeave) {
        await axios.put(`${API}/leaves/${editingLeave.id}`, data, { withCredentials: true });
        toast.success("İzin güncellendi");
      } else {
        await axios.post(`${API}/leaves`, data, { withCredentials: true });
        toast.success("İzin eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu izin kaydını silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/leaves/${id}`, { withCredentials: true });
      toast.success("İzin silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const updateStatus = async (leave, newStatus) => {
    try {
      const data = { ...leave, status: newStatus };
      await axios.put(`${API}/leaves/${leave.id}`, data, { withCredentials: true });
      toast.success(`İzin ${statusLabels[newStatus].toLowerCase()}`);
      fetchData();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    }
  };

  const openEditDialog = (leave) => {
    setEditingLeave(leave);
    setFormData({
      employee_id: leave.employee_id,
      type: leave.type,
      start_date: leave.start_date,
      end_date: leave.end_date,
      days: leave.days,
      status: leave.status,
      notes: leave.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingLeave(null);
    setFormData({
      employee_id: "",
      type: "annual",
      start_date: "",
      end_date: "",
      days: 1,
      status: "pending",
      notes: ""
    });
  };

  const filteredLeaves = leaves.filter(l => {
    const matchesSearch = l.employee_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || l.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leaves-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">İzin Takibi</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Bekleyen: {leaves.filter(l => l.status === "pending").length} | 
            Onaylanan: {leaves.filter(l => l.status === "approved").length}
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
          data-testid="add-leave-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni İzin
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input 
            placeholder="Personel ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800"
            data-testid="leave-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800" data-testid="leave-status-filter">
            <SelectValue placeholder="Durum" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="pending">Bekleyen</SelectItem>
            <SelectItem value="approved">Onaylanan</SelectItem>
            <SelectItem value="rejected">Reddedilen</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Personel</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">İzin Türü</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Tarih</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Gün</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Durum</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeaves.map((leave, index) => (
                <motion.tr 
                  key={leave.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => openEditDialog(leave)}
                  className="border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors cursor-pointer"
                  data-testid={`leave-row-${leave.id}`}
                >
                  <td className="px-4 py-4">
                    <p className="text-sm text-zinc-50">{leave.employee_name}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-zinc-400">{leaveTypes[leave.type] || leave.type}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                      <Calendar className="w-4 h-4" />
                      {leave.start_date} - {leave.end_date}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="text-sm font-mono text-zinc-50">{leave.days}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex justify-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs ${statusColors[leave.status]}`}>
                        {leave.status === 'pending' && <Clock className="w-3 h-3" />}
                        {leave.status === 'approved' && <CheckCircle className="w-3 h-3" />}
                        {leave.status === 'rejected' && <XCircle className="w-3 h-3" />}
                        {statusLabels[leave.status]}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      {leave.status === 'pending' && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); updateStatus(leave, 'approved'); }}
                            className="hover:bg-emerald-400/10 text-zinc-400 hover:text-emerald-400 h-8 w-8"
                            data-testid={`approve-leave-${leave.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); updateStatus(leave, 'rejected'); }}
                            className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400 h-8 w-8"
                            data-testid={`reject-leave-${leave.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Pencil className="w-3 h-3 text-zinc-500" />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDelete(leave.id); }}
                        className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400 h-8 w-8"
                        data-testid={`delete-leave-${leave.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
              {filteredLeaves.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-zinc-500">
                    {search || statusFilter !== "all" ? "Sonuç bulunamadı" : "Henüz izin kaydı eklenmemiş"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-outfit">
              {editingLeave ? "İzin Düzenle" : "Yeni İzin"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Personel *</Label>
                <Select 
                  value={formData.employee_id} 
                  onValueChange={(v) => setFormData({ ...formData, employee_id: v })}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="leave-employee-select">
                    <SelectValue placeholder="Personel seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">İzin Türü *</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => setFormData({ ...formData, type: v })}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="leave-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {Object.entries(leaveTypes).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Başlangıç Tarihi *</Label>
                <Input 
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    start_date: e.target.value,
                    days: calculateDays(e.target.value, formData.end_date)
                  })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="leave-startdate-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Bitiş Tarihi *</Label>
                <Input 
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    end_date: e.target.value,
                    days: calculateDays(formData.start_date, e.target.value)
                  })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="leave-enddate-input"
                />
              </div>
              {editingLeave && (
                <div className="col-span-2">
                  <Label className="text-zinc-400">Durum</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="leave-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="pending">Bekliyor</SelectItem>
                      <SelectItem value="approved">Onaylandı</SelectItem>
                      <SelectItem value="rejected">Reddedildi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2">
                <Label className="text-zinc-400">Notlar</Label>
                <Textarea 
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  rows={2}
                  data-testid="leave-notes-input"
                />
              </div>
            </div>
            <div className="text-sm text-zinc-500">
              Toplam: <span className="text-amber-400 font-mono">{calculateDays(formData.start_date, formData.end_date)}</span> gün
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                İptal
              </Button>
              <Button 
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                data-testid="save-leave-btn"
              >
                {editingLeave ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leaves;
